import { WasmLoader } from '@seddi/core';
import { RecolorModule } from '@seddi/ui-recolor';
import { SceneGeometryModule } from '@seddi/ui-three-viewer';
import WaGarmentsGeometry from '@seddi/wa-garments-geometry';
import WaRender from '@seddi/wa-render';
import log from 'loglevel';

import type { Viewer } from './components/AppViewer/AppViewer';
import { retry } from './core/retry';
import { store } from './store/appStore';

import WaGarmentsGeometryWasm from '@seddi/wa-garments-geometry/lib/index.wasm';
import WaRenderWasm from '@seddi/wa-render/lib/index.wasm';

let sceneGeometryModule: SceneGeometryModule | null = null;

/**
 * Init app.
 */
const init = async (): Promise<void> => {
  log.info('App is initializing');
  // fetch and instantiate Web Assembly modules
  await Promise.all([
    fetch(WaGarmentsGeometryWasm)
      .then(res => res.arrayBuffer())
      .then(arr => WasmLoader.Load(WaGarmentsGeometry, new Uint8Array(arr))),
    fetch(WaRenderWasm)
      .then(res => res.arrayBuffer())
      .then(arr => WasmLoader.Load(WaRender, new Uint8Array(arr))),
    RecolorModule.init(),
  ]);
  // TODO: hide @seddi/ui-three-viewer SceneGeometryModule life-cycle management
  sceneGeometryModule = new SceneGeometryModule();
  // log ready
  log.info('App is ready');
};

/**
 * Wait for app initialization.
 */
export const waitForAppReady = (() => {
  let promise: Promise<void> | null = null;

  return (): Promise<void> => {
    if (promise === null) {
      promise = init();
    }
    return promise;
  };
})();

/**
 * Wait for viewer available in the hierarchy.
 * @param interval Retry interval in milliseconds.
 * @param numRetries Retry max count.
 */
const waitForViewer = (interval = 100, numRetries = 50): Promise<Viewer> => {
  return retry(
    () => {
      const viewerRef = store.getState().appRefs.viewerRef;

      if (viewerRef?.current) {
        return viewerRef.current;
      }

      throw Error('Viewer not found');
    },
    interval,
    numRetries,
  );
};

/**
 * Wait for all scene resources ready promise.
 */
const waitForSceneReadyPromise = async (): Promise<void> => {
  // wait for viewer
  const viewer = await waitForViewer();
  if (!viewer) {
    log.warn('Viewer not found');
    return;
  }
  // check scene resources already loaded
  {
    const resources = viewer.resources;
    if (resources.itemsLoaded === resources.itemsTotal) return;
  }
  // track progress with resources progress events
  return new Promise(resolve => {
    function handleResourcesProgress(event: Event) {
      const progressEvent = event as ProgressEvent;
      if (progressEvent.loaded < progressEvent.total) return;

      // all resources loaded
      viewer.removeEventListener('resourcesprogress', handleResourcesProgress);
      log.info('Scene is ready');
      resolve();
    }
    viewer.addEventListener('resourcesprogress', handleResourcesProgress);
  });
};

/**
 * Wait for all scene resources ready.
 */
export const waitForSceneReady = (() => {
  // single promise at once
  let promise: Promise<void> | null = null;

  return (): Promise<void> => {
    if (promise === null) {
      promise = waitForSceneReadyPromise();
      promise.then(() => {
        setTimeout(() => {
          // remove current promise
          promise = null;
        });
      });
    }
    return promise;
  };
})();

/**
 * Dispose app.
 */
export const dispose = (): Promise<void> => {
  sceneGeometryModule?.geometryCache.dispose();
  return Promise.all([RecolorModule.dispose()]).then(() => {
    return;
  });
};
